import { cn } from "@/utils/utils";

/**
 * Deal type pill plus an OTB indicator (design doc §7.1, §7.3).
 *
 * A TB+GL deal's period is one combined `tb_gl` card on the board, so the pill
 * (`GLTB`) already says what the card covers and no leg chip is shown. The one
 * indicator that still appears on the board is `OTB`: an opening-balance row is
 * its own thing rather than a monthly submission, and without the chip it
 * would look identical to the deal's ordinary rows — on a TB-only deal too.
 *
 * The `TB` / `GL` chip for a single leg of a TB+GL deal is kept for any caller
 * that renders one leg on its own; the board no longer produces such rows.
 */
export interface TSubmissionTypeBadgeProps {
    /** Deal file type as sent by the API (`tb_only` | `tb_and_gl`). */
    dealFileType: string;
    /** File type of this row: one leg (`trial_balance` | `general_ledger`), the
     * deal's opening balance (`open_trial_balance`), or both legs at once
     * (`tb_gl`) — which is how the board shows every TB+GL period. */
    fileType: string;
    className?: string;
}

export function SubmissionTypeBadge({
    dealFileType,
    fileType,
    className,
}: TSubmissionTypeBadgeProps) {
    const isTbAndGl = dealFileType === "tb_and_gl";
    const isOtb = fileType === "open_trial_balance";
    const isBothLegs = fileType === "tb_gl";
    const leg = isOtb ? "OTB" : fileType === "general_ledger" ? "GL" : "TB";
    const showLeg = (isTbAndGl || isOtb) && !isBothLegs;

    return (
        <span className={cn("flex shrink-0 items-center gap-1", className)}>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                {isTbAndGl ? "GLTB" : "TB"}
            </span>
            {showLeg && (
                <span
                    aria-label={`${leg} leg`}
                    className="rounded border border-border px-1 py-px text-[10px] font-semibold uppercase leading-none tracking-wide text-muted-foreground"
                >
                    {leg}
                </span>
            )}
        </span>
    );
}
